import React, { FC, forwardRef, Ref } from 'react';
import cx from 'classnames';

import SectionProvider from '@/contexts/section-context';

type SectionProps = {
	variant: string;
	id?: string;
	className?: string;
	slug: string;
	ref?: Ref<any>;
} & ChildrenProps;

type SectionSubcomponents = {
	Uptitle: FC<SubcomponentProps>;
	Title: FC<SubcomponentTitleProps>;
	Introduction: FC<SubcomponentProps>;
};

type SubcomponentProps = {
	text?: string;
};
type SubcomponentTitleProps = SubcomponentProps & {
	titleTag?: string;
};

/**
 * SUBCOMPONENTS
 */
const Uptitle: FC<SubcomponentProps> = ({ text }) => (
	<p
		className="supt-section__uptitle"
		dangerouslySetInnerHTML={{
			__html: text,
		}}
	/>
);

const Title: FC<SubcomponentTitleProps> = ({ text, titleTag }) => {
	const TitleTag = titleTag as keyof JSX.IntrinsicElements;
	return (
		<TitleTag
			className="supt-section__title"
			dangerouslySetInnerHTML={{ __html: text }}
		/>
	);
};

const Introduction: FC<SubcomponentProps> = ({ text }) => (
	<p
		className="supt-section__introduction"
		dangerouslySetInnerHTML={{
			__html: text,
		}}
	/>
);

interface ForwardedSection
	extends React.ForwardRefExoticComponent<
			SectionProps & React.RefAttributes<HTMLDivElement>
		>,
		SectionSubcomponents {}
/**
 * COMPONENT
 */
export const Section = forwardRef<HTMLDivElement, SectionProps>(
	(
		{ id, slug = '', className, variant, children }: SectionProps,
		ref: Ref<any>
	) => {
		const rootClass = cx(
			'supt-section',
			`supt-section--${variant}`,
			className
		);

		return (
			<SectionProvider variant={variant}>
				<div
					id={id}
					className={rootClass}
					data-block={slug}
					data-variant={variant}
					ref={ref}
				>
					{children}
				</div>
			</SectionProvider>
		);
	}
) as ForwardedSection;

Section.Uptitle = Uptitle;
Section.Title = Title;
Section.Introduction = Introduction;
